/**
 * VIP: what it multiplies, and how the fractions survive.
 *
 * VIP is a time window, not a flag. It is active when
 * `startsAt <= now < endsAt` measured on the SERVER's clock, and it multiplies
 * exactly two things - the XP and the Gold a window already earned. It grants no
 * Cash, no items, no artifacts, no pets, no drop chance, no damage, no health
 * and no attack speed.
 *
 * ## The multipliers are rationals, never floats
 *
 *     XP   x 23 / 20   (+15%)
 *     Gold x  3 /  2   (+50%)
 *
 * Stated as a numerator and a denominator because a balance is a `bigint` and
 * `1.15` is not representable in binary floating point. `23n * value / 20n`
 * truncates; over thousands of small rewards that truncation is a real, growing
 * theft of the bonus a player paid for.
 *
 * ## Which is why there is a carry
 *
 * Every application keeps the REMAINDER and feeds it into the next one:
 *
 *     total = (base * numerator + carry) / denominator     (integer division)
 *     carry = (base * numerator + carry) % denominator
 *
 * Twenty rewards of 1 XP produce 460 / 20 = 23 XP, which is exactly +15% - not
 * twenty truncations to 1. The carry is authoritative state, stored per account,
 * so it survives a checkpoint, an F5 and a server restart. It is bounded by the
 * denominator and therefore can never grow.
 */

/** An exact rational multiplier. Never a float, anywhere near a balance. */
export interface VipMultiplier {
  readonly numerator: bigint;
  readonly denominator: bigint;
}

/** +15% XP, exactly. */
export const VIP_XP_MULTIPLIER: VipMultiplier = Object.freeze({
  numerator: 23n,
  denominator: 20n,
});

/** +50% Gold, exactly. */
export const VIP_GOLD_MULTIPLIER: VipMultiplier = Object.freeze({
  numerator: 3n,
  denominator: 2n,
});

/** What the interface shows. Derived from the rationals, never typed twice. */
export const VIP_XP_BONUS_PERCENT = 15;
export const VIP_GOLD_BONUS_PERCENT = 50;

/** The result of one application: what to credit, and what to carry forward. */
export interface VipBonusResult {
  /** The full amount to credit, base included. Never less than `base`. */
  readonly total: bigint;
  /** The remainder to feed into the next application. Always < denominator. */
  readonly carry: bigint;
}

/**
 * Applies a VIP multiplier to an already-authorised amount.
 *
 * `base` is what the window earned under the existing rules - the simulation's
 * own total, after artifacts and after any offline reduction. This multiplies
 * that and nothing else, exactly once.
 *
 * Refuses a negative base or a negative carry rather than quietly producing a
 * smaller reward: both would mean a caller has confused a delta for a balance.
 */
export function applyVipMultiplier(
  base: bigint,
  carry: bigint,
  multiplier: VipMultiplier,
): VipBonusResult {
  if (base < 0n) {
    throw new RangeError(`VIP base must not be negative, received ${base}`);
  }
  if (carry < 0n || carry >= multiplier.denominator) {
    throw new RangeError(`VIP carry must be in [0, ${multiplier.denominator}), received ${carry}`);
  }
  const scaled = base * multiplier.numerator + carry;
  return {
    total: scaled / multiplier.denominator,
    carry: scaled % multiplier.denominator,
  };
}

/* -------------------------------------------------------------------------- */
/*  The window                                                                */
/* -------------------------------------------------------------------------- */

/** A VIP entitlement's own span, in server time. */
export interface VipWindow {
  readonly startsAt: Date;
  readonly endsAt: Date;
}

/**
 * Is VIP active at this instant?
 *
 * Half-open on purpose: the second a subscription ends it is over, and a window
 * that starts exactly now is already running. Two adjacent entitlements
 * therefore never both cover the same millisecond.
 */
export function isVipActiveAt(window: VipWindow, at: Date): boolean {
  return window.startsAt.getTime() <= at.getTime() && at.getTime() < window.endsAt.getTime();
}

/**
 * The single window several entitlements add up to.
 *
 * Buffs never stack, so owning two overlapping entitlements is worth exactly as
 * much as owning one: the state is "active until the coverage runs out".
 * Returns null when nothing covers `at`.
 *
 * Coverage is followed through CONTIGUOUS entitlements, not only overlapping
 * ones, and that is the whole point rather than a detail. A renewal starts
 * exactly where the previous window ended, so it never contains the instant
 * being asked about; considering only the windows that do would report the
 * first end as the last one - and `vipRenewalStart` would then chain the NEXT
 * renewal onto a period the account already owns, silently destroying every day
 * bought in between. Half-open spans make `startsAt <= end` mean "no gap".
 */
export function mergeVipWindows(windows: readonly VipWindow[], at: Date): VipWindow | null {
  const covering = windows.filter((window) => isVipActiveAt(window, at));
  if (covering.length === 0) {
    return null;
  }
  let startMs = Math.min(...covering.map((window) => window.startsAt.getTime()));
  let endMs = Math.max(...covering.map((window) => window.endsAt.getTime()));

  // Grow the span while any entitlement touches it, in either direction. The
  // list is bounded by the account's own entitlements, and each pass either
  // extends the span or stops, so this terminates in at most `windows.length`
  // passes.
  for (let pass = 0; pass < windows.length; pass += 1) {
    let grew = false;
    for (const window of windows) {
      const wStart = window.startsAt.getTime();
      const wEnd = window.endsAt.getTime();
      // Touching or overlapping in either direction: no gap between them.
      if (wStart > endMs || wEnd < startMs) {
        continue;
      }
      if (wEnd > endMs) {
        endMs = wEnd;
        grew = true;
      }
      if (wStart < startMs) {
        startMs = wStart;
        grew = true;
      }
    }
    if (!grew) {
      break;
    }
  }
  return { startsAt: new Date(startMs), endsAt: new Date(endMs) };
}

/**
 * Where a renewal starts.
 *
 * Renewing while active extends FROM THE CURRENT END, so no purchased day is
 * ever thrown away; renewing after expiry starts now, so nobody is billed for
 * time that already passed. `currentEnd` is null when there is no live window.
 */
export function vipRenewalStart(currentEnd: Date | null, now: Date): Date {
  if (currentEnd && currentEnd.getTime() > now.getTime()) {
    return currentEnd;
  }
  return now;
}

/** Seconds left on a window, floored at zero. Never negative, never a lie. */
export function vipRemainingSeconds(window: VipWindow | null, at: Date): number {
  if (!window) {
    return 0;
  }
  return Math.max(0, Math.floor((window.endsAt.getTime() - at.getTime()) / 1000));
}

/* -------------------------------------------------------------------------- */
/*  Windows that cross an expiry                                              */
/* -------------------------------------------------------------------------- */

/** How a stretch of time divides across a VIP expiry. */
export interface VipTimeSplit {
  /** Milliseconds of the stretch during which VIP was active. */
  readonly vipMs: number;
  /** Milliseconds during which it was not. */
  readonly plainMs: number;
}

/**
 * Divides a stretch of credited time at the moment VIP ended.
 *
 * Offline farm can cover six hours; a subscription that expired two hours in
 * must pay the bonus on two hours and nothing on the other four. The split is
 * arithmetic on the server's own instants - never a boolean the client sends,
 * and never "was VIP active when you came back".
 *
 * Both ends are clamped to the stretch, so an entitlement that started after it
 * finished or ended before it began contributes zero rather than a negative.
 */
export function splitByVipExpiry(
  fromMs: number,
  toMs: number,
  window: VipWindow | null,
): VipTimeSplit {
  const totalMs = Math.max(0, toMs - fromMs);
  if (!window || totalMs === 0) {
    return { vipMs: 0, plainMs: totalMs };
  }
  const overlapStart = Math.max(fromMs, window.startsAt.getTime());
  const overlapEnd = Math.min(toMs, window.endsAt.getTime());
  const vipMs = Math.max(0, Math.min(totalMs, overlapEnd - overlapStart));
  return { vipMs, plainMs: totalMs - vipMs };
}

/**
 * Splits an amount in proportion to a time split, exactly.
 *
 * The VIP share is floored and the remainder goes to the plain share, so the
 * two always add back to the original: an idle payout can never gain or lose a
 * unit to rounding. Returns zeros for an empty stretch rather than dividing by
 * it.
 */
export function splitAmountByTime(
  amount: bigint,
  split: VipTimeSplit,
): {
  readonly vipPart: bigint;
  readonly plainPart: bigint;
} {
  const totalMs = split.vipMs + split.plainMs;
  if (totalMs <= 0 || amount <= 0n) {
    return { vipPart: 0n, plainPart: amount > 0n ? amount : 0n };
  }
  const vipPart = (amount * BigInt(split.vipMs)) / BigInt(totalMs);
  return { vipPart, plainPart: amount - vipPart };
}
